import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertCircle, Download, Sparkles } from 'lucide-react';

const BadgeGenerator = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [badgeUrl, setBadgeUrl] = useState(null);

    const generateBadge = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/generate-badge', {
                method: 'POST',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to generate badge');
            }

            setBadgeUrl(data.image);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-md mx-auto mt-8 p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
            <div className="text-center space-y-4">
                <h3 className="text-xl font-bold text-white flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    Daily Badge Generator
                </h3>
                <p className="text-sm text-gray-400">
                    Create your unique daily $TITSTR badge. Only one per day!
                </p>

                <AnimatePresence mode="wait">
                    {error && (
                        <motion.div
                            key="error-message"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2 justify-center"
                        >
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </motion.div>
                    )}

                    {badgeUrl ? (
                        <motion.div
                            key="badge-display"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="space-y-4"
                        >
                            <div className="relative aspect-square w-full rounded-xl overflow-hidden border-2 border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.2)]">
                                <img src={badgeUrl} alt="Daily Badge" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex gap-3 justify-center">
                                <a
                                    href={badgeUrl}
                                    download={`titstr-badge-${new Date().toISOString().split('T')[0]}.png`}
                                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors font-medium text-sm"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </a>
                            </div>
                            <p className="text-xs text-green-400 font-medium">
                                Badge generated successfully! Come back tomorrow.
                            </p>
                        </motion.div>
                    ) : (
                        <motion.button
                            key="generate-button"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={generateBadge}
                            disabled={loading}
                            className={`w-full py-3 px-4 rounded-xl font-bold text-white shadow-lg transition-all
                                ${loading
                                    ? 'bg-gray-600 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-purple-500/25'
                                }`}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Generating Magic...
                                </span>
                            ) : (
                                'Generate My Badge'
                            )}
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default BadgeGenerator;
